import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import pagesService from '../services/pagesService';
import schedulesService from '../services/schedulesService';
import { normalizePageData } from '../utils/pageDataNormalizer';
import { normalizeSocialLinks, normalizeGenericLinks } from '../utils/socialLinksNormalizer';
import LinkInBioForm from '../components/pages/LinkInBioForm';
import LinkInBioPreview from '../components/pages/LinkInBioPreview';
import HtmlTemplatePreview from '../components/pages/HtmlTemplatePreview';
import PageCreatedModal from '../components/pages/PageCreatedModal';
import MobilePreviewFab from '../components/pages/MobilePreviewFab';
import useSnackbar from '../hooks/useSnackbar';

/**
 * PageEditorPage - Editor de páginas com 2 colunas (form + preview)
 * 
 * Suporta:
 * - Criação: /pages/new/link-in-bio (pageType na URL)
 * - Edição: /pages/edit/:pageId (carrega dados existentes)
 */
export default function PageEditorPage() {
  const { pageType, pageId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSuccess, showError } = useSnackbar();

  // Estados
  const [loading, setLoading] = useState(!!pageId); // Carregar se editando
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [existingPage, setExistingPage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdSlug, setCreatedSlug] = useState('');
  const [createdShortId, setCreatedShortId] = useState('');

  // Form com validação Zod
  const formSchema = z.object({
    title: z.string().min(1, 'Título é obrigatório').max(100, 'Máximo 100 caracteres'),
    subtitle: z.string().max(3000, 'Máximo 3000 caracteres').optional().nullable(),
    logoUrl: z.string().nullable().optional(),
    avatarShape: z.enum(['round', 'free']).default('round').optional(),
    slug: z.string().min(1, 'Slug é obrigatório').max(50, 'Máximo 50 caracteres'),
    shortId: z.string().optional(),
    templateId: z.string().min(1, 'Template é obrigatório'),
    scheduleId: z.string().min(1, 'Agenda é obrigatória'),
    socialLinks: z.object({
      instagram: z.object({ url: z.string().url('URL inválida').optional().or(z.literal('')) }).optional(),
      twitter: z.object({ url: z.string().url('URL inválida').optional().or(z.literal('')) }).optional(),
      linkedin: z.object({ url: z.string().url('URL inválida').optional().or(z.literal('')) }).optional(),
      youtube: z.object({ url: z.string().url('URL inválida').optional().or(z.literal('')) }).optional(),
      threads: z.object({ url: z.string().url('URL inválida').optional().or(z.literal('')) }).optional(),
      facebook: z.object({ url: z.string().url('URL inválida').optional().or(z.literal('')) }).optional(),
    }).optional(),
    genericLinks: z.array(
      z.object({
        url: z.string().url('URL inválida').optional().or(z.literal('')),
        name: z.string().max(100).optional(),
      })
    ).optional(),
    customStyles: z.object({
      backgroundColor: z.string().optional(),
      textColor: z.string().optional(),
      fontFamily: z.string().optional(),
      fontSize: z.string().optional(),
      linkColor: z.string().optional(),
      linkBackground: z.string().optional(),
      cardBackground: z.string().optional(),
    }).optional(),
    agendaLinkName: z.string().max(50).optional(),
    scheduleName: z.string().optional(),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      subtitle: '',
      logoUrl: null,
      avatarShape: 'round',
      slug: '',
      shortId: '',
      templateId: '',
      scheduleId: '',
      socialLinks: {
        instagram: { url: '' },
        twitter: { url: '' },
        linkedin: { url: '' },
        youtube: { url: '' },
        threads: { url: '' },
        facebook: { url: '' },
      },
      genericLinks: [
        { url: '', name: '' },
        { url: '', name: '' },
        { url: '', name: '' },
        { url: '', name: '' },
        { url: '', name: '' },
      ],
      customStyles: {
        backgroundColor: '#ffffff',
        textColor: '#000000',
        fontFamily: 'Roboto',
        fontSize: 'medium',
        linkColor: '#1976d2',
        linkBackground: '#f5f5f5',
        cardBackground: '#ffffff',
      },
      scheduleName: '',
      agendaLinkName: '',
    },
  });

  // Carregar templates, agendas e página existente
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Carregar templates do tipo de página
      const templatesResponse = await pagesService.getTemplates(pageType || 'link-in-bio');
      setTemplates(Array.isArray(templatesResponse) ? templatesResponse : templatesResponse.$values || []);

      // 2. Carregar agendas do usuário
      try {
        const schedulesResponse = await schedulesService.getAll();
        
        // schedulesService.getAll() already filters e retorna apenas agendas ativas
        // Se for array, use direto. Se for objeto com $values, extrai.
        const schedulesArray = Array.isArray(schedulesResponse) 
          ? schedulesResponse 
          : (schedulesResponse?.$values || []);
        
        setSchedules(schedulesArray);
      } catch (scheduleError) {
        console.error('Erro ao carregar agendas:', scheduleError);
        // Não falha totalmente, só deixa agendas vazias
        setSchedules([]);
      }

      // 3. Se editando, carregar dados da página
      if (pageId) {
        const pageResponse = await pagesService.getById(pageId);
        setExistingPage(pageResponse);

        // Normalizar fontSize para chave válida do Select
        const normalizeFontSize = (value) => {
          if (['small', 'medium', 'large', 'extra-large'].includes(value)) {
            return value;
          }
          // Mapear pixels para chaves
          const pixelToKey = {
            '14px': 'small',
            '16px': 'medium',
            '18px': 'large',
            '20px': 'extra-large',
          };
          return pixelToKey[value] || 'medium';
        };

        // Preencher formulário com dados existentes
        form.reset({
          title: pageResponse.title || '',
          subtitle: pageResponse.config?.subtitle || '',
          logoUrl: pageResponse.config?.logoUrl || null,
          avatarShape: pageResponse.config?.avatarShape || 'round',
          slug: pageResponse.slug || '',
          shortId: pageResponse.shortId || '',
          templateId: pageResponse.templateId || '',
          scheduleId: pageResponse.config?.scheduleId || '',
          scheduleName: pageResponse.config?.scheduleName || '',
          agendaLinkName: pageResponse.config?.agendaLinkName || '',
          socialLinks: pageResponse.config?.socialLinks || {},
          genericLinks: pageResponse.config?.genericLinks || [],
          customStyles: {
            backgroundColor: pageResponse.config?.customStyles?.backgroundColor || '#ffffff',
            textColor: pageResponse.config?.customStyles?.textColor || '#000000',
            fontFamily: pageResponse.config?.customStyles?.fontFamily || 'Roboto',
            fontSize: normalizeFontSize(pageResponse.config?.customStyles?.fontSize),
            linkColor: pageResponse.config?.customStyles?.linkColor || '#1976d2',
            linkBackground: pageResponse.config?.customStyles?.linkBackground || '#f5f5f5',
            cardBackground: pageResponse.config?.customStyles?.cardBackground || '#ffffff',
          },
        });
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.message || err.message);
      showError('Erro ao carregar dados do editor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [pageType, pageId]);

  // Usar useWatch para observar campos relevantes para o preview de forma otimizada
  const watchedFields = useWatch({
    control: form.control,
    name: ['title', 'subtitle', 'templateId', 'logoUrl', 'avatarShape', 'socialLinks', 'genericLinks', 'customStyles', 'scheduleId', 'scheduleName', 'agendaLinkName'],
  });

  // Memorizar os dados do preview para evitar re-renders desnecessários
  const previewData = useMemo(() => {
    const [title, subtitle, templateId, logoUrl, avatarShape, socialLinks, genericLinks, customStyles, scheduleId, scheduleName, agendaLinkName] = watchedFields;
    
    // Encontrar nome da agenda selecionada (para popular automaticamente se vazio)
    const selectedSchedule = schedules.find(s => s.id === scheduleId);
    const finalScheduleName = scheduleName?.trim() || selectedSchedule?.name || 'Agendar';
    
    return {
      title: title || '',
      subtitle: subtitle || '',
      templateId: templateId || '',
      logoUrl: logoUrl || '',
      avatarShape: avatarShape || 'round',
      socialLinks: socialLinks || {},
      genericLinks: genericLinks || [],
      customStyles: customStyles || {},
      scheduleId: scheduleId || '',
      scheduleName: finalScheduleName,
      agendaLinkName: agendaLinkName || '',
    };
  }, [watchedFields, schedules, templates]);

  // Transform formData to ensure socialLinks and genericLinks have proper structure
  const getEnhancedFormData = (formData) => {
    // Normalize and ensure socialLinks have NO name property (only URL)
    const normalizedSocialLinks = normalizeSocialLinks(formData.socialLinks);
    
    // Normalize and ensure genericLinks have proper structure (name and url)
    const normalizedGenericLinks = normalizeGenericLinks(formData.genericLinks);
    const enhancedGenericLinks = (normalizedGenericLinks || []).map((link) => ({
      ...link,
      name: link.name || '',
      url: link.url || '',
    }));

    return {
      ...formData,
      socialLinks: normalizedSocialLinks,
      genericLinks: enhancedGenericLinks,
    };
  };

  // Get selected template from templates array
  const getSelectedTemplate = () => {
    const templateId = form.watch('templateId');
    return templates.find(t => t.id === templateId);
  };

  // Enviar formulário (com normalização genérica e tratamento de erros)
  const onSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setError(null);

      // Log pre-submit summary
      console.log('[PageEditorPage] FORM SUBMIT:', {
        title: formData.title,
        slug: formData.slug,
        template: formData.templateId,
        schedule: formData.scheduleId,
        socialLinks: Object.keys(formData.socialLinks || {}).length,
        genericLinks: formData.genericLinks?.filter(l => l.url)?.length || 0,
      });

      // TODO 10.2: Normalizar dados de forma GENÉRICA
      // Mantém compatibilidade com qualquer tipo de página (link-in-bio, clinic, professional)
      console.log('[PageEditorPage] Iniciando normalizePageData...');
      let normalizedPayload;
      try {
        normalizedPayload = normalizePageData(formData, pageType || 'link-in-bio');
        console.log('[PageEditorPage] normalizePageData sucesso:', normalizedPayload);
      } catch (normalizerErr) {
        console.error('[PageEditorPage] ERRO EM normalizePageData:', normalizerErr);
        throw new Error(`Erro ao processar dados: ${normalizerErr.message}`);
      }

      let response;
      if (pageId) {
        // ===== EDIÇÃO =====
        // PUT /api/pages/{id} - GENÉRICO, sem rota específica
        console.log('[PageEditorPage] Iniciando UPDATE...');
        response = await pagesService.update(pageId, normalizedPayload);
        console.log('[PageEditorPage] UPDATE sucesso:', response);
        
        showSuccess('Página atualizada com sucesso!');
        
        // Redirect após 1.5s para permitir feedback visual
        setTimeout(() => navigate('/pages'), 1500);
      } else {
        // ===== CRIAÇÃO =====
        // POST /api/pages - GENÉRICO, não /api/pages/link-in-bio
        console.log('[PageEditorPage] Iniciando CREATE com payload:', normalizedPayload);
        try {
          response = await pagesService.create(normalizedPayload);
          console.log('[PageEditorPage] CREATE sucesso, response:', response);
        } catch (createErr) {
          console.error('[PageEditorPage] CREATE ERRO:', {
            status: createErr.response?.status,
            data: createErr.response?.data,
            message: createErr.message
          });
          throw createErr;
        }
        
        // Exibir modal de sucesso com URL pública
        setCreatedSlug(response.slug);
        setCreatedShortId(response.shortId);
        setShowSuccessModal(true);
      }

      // Log estruturado para debug
      console.info('[PageEditorPage] Página salva com sucesso', {
        pageType,
        isEdit: !!pageId,
        slug: normalizedPayload.slug,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('[PageEditorPage] ❌ ERRO AO SALVAR PÁGINA', {
        pageType,
        isEdit: !!pageId,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        stack: err.stack
      });

      // TODO 10.4: Tratamento específico de erros da API
      const errorMsg = extractApiErrorMessage(err);
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Extrair mensagem de erro da resposta API
   * Trata diferentes formatos de erro do backend
   */
  const extractApiErrorMessage = (error) => {
    // Erro de rede
    if (!error.response) {
      return 'Erro de conexão. Tente novamente.';
    }

    const { status, data } = error.response;

    // 400 Bad Request - Validação
    if (status === 400) {
      if (data.errors && typeof data.errors === 'object') {
        const errorMessages = Object.values(data.errors)
          .flat()
          .join('; ');
        return errorMessages || data.message || 'Validação falhou';
      }
      return data.message || 'Dados inválidos';
    }

    // 409 Conflict - Slug duplicado
    if (status === 409) {
      if (data.message?.includes('slug') || data.message?.includes('Slug')) {
        return 'Este slug já está em uso. Tente outro.';
      }
      return data.message || 'Conflito ao salvar. Tente novamente.';
    }

    // 404 Not Found - Página deletada ou template removido
    if (status === 404) {
      if (data.message?.includes('template') || data.message?.includes('Template')) {
        return 'Template não encontrado. Escolha outro template.';
      }
      return 'Página não encontrada. Pode ter sido deletada.';
    }

    // 401/403 - Permissões
    if (status === 401 || status === 403) {
      return 'Você não tem permissão para realizar esta ação.';
    }

    // Erro genérico
    return data.message || `Erro ao salvar página (${status})`;
  };

  // Estados de carregamento e erro
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        bgcolor: '#fafafa',
      }}
    >
      {/* Coluna esquerda - Formulário */}
      <Box
        sx={{
          width: isMobile ? '100%' : '50%',
          overflowY: 'auto',
          p: 3,
          borderRight: isMobile ? 'none' : '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
        }}
      >
        {/* DEBUG: Mostrar erros de validação */}
        {Object.keys(form.formState.errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Erros de validação:</strong>
            <ul>
              {Object.entries(form.formState.errors).map(([field, error]) => (
                <li key={field}>{field}: {error?.message || 'erro desconhecido'}</li>
              ))}
            </ul>
          </Alert>
        )}

        <LinkInBioForm
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          submitting={submitting}
          templates={templates}
          schedules={schedules}
          isEdit={!!pageId}
        />
      </Box>

      {/* Coluna direita - Preview (escondido em mobile) */}
      {!isMobile && (
        <Box
          sx={{
            width: '50%',
            overflowY: 'auto',
            bgcolor: '#f0f0f0',
            p: 3,
          }}
        >
          {/* Usar HtmlTemplatePreview se template URL disponível (renderiza template Static centralizado) */}
          {getSelectedTemplate()?.htmlUrl ? (
            <HtmlTemplatePreview
              templateUrl={getSelectedTemplate()?.htmlUrl}
              formData={previewData}
            />
          ) : (
            /* Fallback para LinkInBioPreview (legacy React component) */
            <LinkInBioPreview
              formData={getEnhancedFormData(previewData)}
              templates={templates}
            />
          )}
        </Box>
      )}

      {/* FAB de preview para mobile */}
      <MobilePreviewFab
        visible={isMobile}
        selectedTemplate={getSelectedTemplate()}
        previewData={previewData}
        templates={templates}
        getEnhancedFormData={getEnhancedFormData}
      />

      {/* Modal de sucesso */}
      <PageCreatedModal
        open={showSuccessModal}
        slug={createdSlug}
        shortId={createdShortId}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/pages');
        }}
        onViewPage={() => {
          setShowSuccessModal(false);
          navigate('/pages');
        }}
      />
    </Box>
  );
}
